import { prefersReducedMotion } from "./motion";
import type { Teardown } from "./registry";

export type TrackOptions = {
	/** The scroll container. */
	track: HTMLElement;
	prev: HTMLElement;
	next: HTMLElement;
	/** Matches one item. Its width plus its parent's column gap is one scroll step. */
	item: string;
};

const DISABLED = "aria-disabled";

/* Sub-pixel track widths mean scrollLeft never lands exactly on scrollWidth - clientWidth. */
const EDGE = 1;

/** Steps a horizontal scroll container by one item, and reflects each end on the two buttons. */
export const createTrack = ({ track, prev, next, item: itemSelector }: TrackOptions): Teardown => {
	// Restored on teardown, so a re-initialised block does not inherit a scrolled-to-end state.
	const authored = new Map<HTMLElement, string | null>([
		[prev, prev.getAttribute(DISABLED)],
		[next, next.getAttribute(DISABLED)],
	]);

	const listeners = new AbortController();
	const { signal } = listeners;

	const step = () => {
		const item = track.querySelector<HTMLElement>(itemSelector);

		if (!item || !(item.parentElement instanceof HTMLElement)) return track.clientWidth;

		const gap = Number.parseFloat(getComputedStyle(item.parentElement).columnGap);

		return item.offsetWidth + (Number.isFinite(gap) ? gap : 0);
	};

	const sync = () => {
		const furthest = track.scrollWidth - track.clientWidth;

		prev.setAttribute(DISABLED, String(track.scrollLeft <= EDGE));
		next.setAttribute(DISABLED, String(track.scrollLeft >= furthest - EDGE));
	};

	const move = (direction: 1 | -1) => (event: Event) => {
		const button = event.currentTarget;

		if (button instanceof HTMLElement && button.getAttribute(DISABLED) === "true") return;

		track.scrollBy({
			left: direction * step(),
			behavior: prefersReducedMotion() ? "auto" : "smooth",
		});
	};

	prev.addEventListener("click", move(-1), { signal });
	next.addEventListener("click", move(1), { signal });
	track.addEventListener("scroll", sync, { passive: true, signal });
	window.addEventListener("resize", sync, { signal });

	sync();

	return () => {
		listeners.abort();

		for (const [button, value] of authored) {
			if (value === null) button.removeAttribute(DISABLED);
			else button.setAttribute(DISABLED, value);
		}
	};
};
